import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from './shared/components/header/header.component';
import { AccessibilityPanelComponent } from './shared/components/accessibility-panel/accessibility-panel.component';
import { ToasterComponent } from './shared/components/toaster/toaster.component';
import { LogoutService } from './services/logout.service';
import { CommonModule } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';

@Component({
  imports: [CommonModule, RouterModule, HeaderComponent, AccessibilityPanelComponent, ToasterComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected title = 'NHA';
  
  // Flag to show logout overlay after password change
  isLoggingOutAfterPasswordChange = false;
  
  private logoutService = inject(LogoutService);
  private keycloak = inject(KeycloakService);
  
  private justDetectedPasswordChange = false;
  
  constructor() {
    // Check for password change redirect immediately in constructor
    // This runs before keycloak-angular processes and clears the URL hash params
    this.checkPasswordChangeRedirect();
    
    // Check if we need to force logout after Keycloak auto-logged in
    // BUT only if we didn't just detect a password change (to avoid infinite loop)
    if (!this.justDetectedPasswordChange) {
      this.checkPendingPasswordChangeLogout();
    }
  }
  
  /**
   * Check if a password change logout is pending after Keycloak auto-logged in.
   * This handles the case where Keycloak's check-sso re-authenticates the user
   * immediately after logout from a password change.
   */
  private checkPendingPasswordChangeLogout(): void {
    const logoutPendingData = localStorage.getItem('password-changed-logout-pending');
    let logoutPending = null;
    
    try {
      logoutPending = logoutPendingData ? JSON.parse(logoutPendingData) : null;
    } catch (e) {
      console.error('[App] Error parsing logout pending flag:', e);
    }
    
    // Check if flag exists and is not older than 2 minutes (to prevent stale logouts)
    const isPending = logoutPending?.pending && 
                      (Date.now() - logoutPending.timestamp < 2 * 60 * 1000);
    
    if (isPending) {
      console.log('[App] Detected pending password change logout - forcing logout now');
      
      // Clear the flag immediately to prevent loops
      localStorage.removeItem('password-changed-logout-pending');
      
      // Show overlay
      this.isLoggingOutAfterPasswordChange = true;
      
      // Force logout IMMEDIATELY (no delay) to prevent Keycloak from initializing
      console.log('[App] Forcing logout after auto-login...');
      // Force immediate logout by clearing tokens and redirecting directly to Keycloak logout
      // This bypasses Keycloak's confirmation dialog
      this.forceImmediateLogout();
    }
  }
  
  /**
   * Check if user was redirected back after changing password in Keycloak.
   * Uses two detection methods:
   * 1. Keycloak's kc_action_status=success in URL (works on staging)
   * 2. localStorage flag + Keycloak auth response (fallback for dev)
   */
  private checkPasswordChangeRedirect(): void {
    const fullUrl = window.location.href;
    const hashPart = window.location.hash || '';
    
    // Method 1: Check for Keycloak's kc_action_status=success (staging)
    const hasPasswordAction = fullUrl.includes('kc_action=UPDATE_PASSWORD');
    const hasSuccessStatus = fullUrl.includes('kc_action_status=success');
    const keycloakReportsSuccess = hasPasswordAction && hasSuccessStatus;
    
    // Method 2: Check localStorage flag + Keycloak auth response (dev fallback)
    const passwordChangeData = localStorage.getItem('keycloak_password_change_flow');
    let localStorageFlag = null;
    try {
      localStorageFlag = passwordChangeData ? JSON.parse(passwordChangeData) : null;
    } catch (e) {
      console.error('[App] Error parsing localStorage flag:', e);
    }
    
    // Check if we have a valid localStorage flag (not older than 10 minutes)
    const hasValidFlag = localStorageFlag?.initiated && 
                        (Date.now() - localStorageFlag.timestamp < 10 * 60 * 1000);
    
    // Check if URL has Keycloak authentication response (indicates successful return from Keycloak)
    const hasKeycloakResponse = hashPart.includes('state=') && hashPart.includes('code=');
    
    const localStorageReportsSuccess = hasValidFlag && hasKeycloakResponse;
    
    console.log('[App] Checking password change redirect:', {
      fullUrl: fullUrl.substring(0, 250),
      keycloakReportsSuccess,
      hasPasswordAction,
      hasSuccessStatus,
      localStorageReportsSuccess,
      hasValidFlag,
      hasKeycloakResponse,
      localStorageFlag
    });
    
    // Trigger logout if EITHER method detects password change
    if (keycloakReportsSuccess || localStorageReportsSuccess) {
      console.log('[App] Password change SUCCESS detected, initiating logout...', {
        method: keycloakReportsSuccess ? 'keycloak_params' : 'localStorage_flag'
      });
      
      // Set flag to prevent checking pending logout in the same constructor call
      this.justDetectedPasswordChange = true;
      
      // Clear the localStorage flag
      localStorage.removeItem('keycloak_password_change_flow');
      
      // Show logout overlay
      this.isLoggingOutAfterPasswordChange = true;
      
      // Clean up the URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      
      // Execute logout IMMEDIATELY (no setTimeout) because keycloak.init's onAuthSuccess
      // will trigger a page reload which would cancel any setTimeout
      console.log('[App] Executing logout after password change...');
      
      // Set a localStorage flag to prevent automatic re-login after logout
      // Using localStorage because sessionStorage is cleared when redirecting to Keycloak domain
      // This flag will be checked after Keycloak auto-logs in
      localStorage.setItem('password-changed-logout-pending', JSON.stringify({
        pending: true,
        timestamp: Date.now()
      }));
      console.log('[App] Set localStorage flag: password-changed-logout-pending');
      
      // Force immediate logout by clearing tokens and redirecting directly to Keycloak logout
      // This bypasses Keycloak's confirmation dialog
      this.forceImmediateLogout();
    }
  }

  /**
   * Forces immediate logout by clearing tokens and redirecting to Keycloak logout
   * This bypasses Keycloak's logout confirmation dialog
   */
  private forceImmediateLogout(): void {
    try {
      // Get Keycloak instance
      const kcInstance = this.keycloak.getKeycloakInstance();
      
      // Clear all Keycloak tokens
      kcInstance.clearToken();
      
      // Clear any stored tokens in localStorage
      const tokenKeysToRemove = ['iudx-ui-cat-auth-token', 'ai-sandbox-cat-auth-token'];
      tokenKeysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore
        }
      });
      
      // Construct Keycloak logout URL with redirect_uri parameter
      // This ensures the logout happens without showing a confirmation dialog
      const logoutUrl = kcInstance.createLogoutUrl({
        redirectUri: window.location.origin
      });
      
      console.log('[App] Redirecting to Keycloak logout:', logoutUrl);
      
      // Redirect to Keycloak logout
      window.location.href = logoutUrl;
    } catch (error) {
      console.error('[App] Error during force logout:', error);
      // Fallback: just redirect to home
      localStorage.removeItem('password-changed-logout-pending');
      window.location.href = '/';
    }
  }
}
